import React, { useState } from 'react';
import { statementAPI } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import { toast } from 'sonner';

const AddStatementModal = ({ open, onClose, onSuccess, companyId, currency }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    period: '',
    period_type: 'quarterly',
    income_statement: {
      revenue: 0,
      cost_of_goods_sold: 0,
      gross_profit: 0,
      operating_expenses: 0,
      operating_income: 0,
      interest_expense: 0,
      tax_expense: 0,
      net_income: 0
    },
    balance_sheet: {
      cash: 0,
      accounts_receivable: 0,
      inventory: 0,
      total_current_assets: 0,
      property_equipment: 0,
      total_assets: 0,
      accounts_payable: 0,
      short_term_debt: 0,
      total_current_liabilities: 0,
      long_term_debt: 0,
      total_liabilities: 0,
      shareholders_equity: 0,
      retained_earnings: 0,
      total_equity: 0
    },
    cash_flow: {
      operating_cash_flow: 0,
      investing_cash_flow: 0,
      financing_cash_flow: 0,
      net_change_in_cash: 0,
      beginning_cash: 0,
      ending_cash: 0
    }
  });

  const updateIncomeStatement = (field, value) => {
    const numValue = parseFloat(value) || 0;
    const newIncome = { ...formData.income_statement, [field]: numValue };
    
    // Auto-calculate derived fields
    newIncome.gross_profit = newIncome.revenue - newIncome.cost_of_goods_sold;
    newIncome.operating_income = newIncome.gross_profit - newIncome.operating_expenses;
    newIncome.net_income = newIncome.operating_income - newIncome.interest_expense - newIncome.tax_expense;
    
    setFormData({ ...formData, income_statement: newIncome });
  };

  const updateBalanceSheet = (field, value) => {
    const numValue = parseFloat(value) || 0;
    const newBalance = { ...formData.balance_sheet, [field]: numValue };
    
    // Auto-calculate totals
    newBalance.total_current_assets = newBalance.cash + newBalance.accounts_receivable + newBalance.inventory;
    newBalance.total_assets = newBalance.total_current_assets + newBalance.property_equipment;
    newBalance.total_current_liabilities = newBalance.accounts_payable + newBalance.short_term_debt;
    newBalance.total_liabilities = newBalance.total_current_liabilities + newBalance.long_term_debt;
    newBalance.total_equity = newBalance.shareholders_equity + newBalance.retained_earnings;
    
    setFormData({ ...formData, balance_sheet: newBalance });
  };

  const updateCashFlow = (field, value) => {
    const numValue = parseFloat(value) || 0;
    const newCashFlow = { ...formData.cash_flow, [field]: numValue };
    
    // Auto-calculate net change
    newCashFlow.net_change_in_cash = newCashFlow.operating_cash_flow + newCashFlow.investing_cash_flow + newCashFlow.financing_cash_flow;
    newCashFlow.ending_cash = newCashFlow.beginning_cash + newCashFlow.net_change_in_cash;
    
    setFormData({ ...formData, cash_flow: newCashFlow });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.period) {
      toast.error('Please enter a period');
      return;
    }

    setLoading(true);

    try {
      await statementAPI.create(companyId, {
        company_id: companyId,
        ...formData
      });
      
      // Reset form
      setFormData({
        period: '',
        period_type: 'quarterly',
        income_statement: {
          revenue: 0, cost_of_goods_sold: 0, gross_profit: 0, operating_expenses: 0,
          operating_income: 0, interest_expense: 0, tax_expense: 0, net_income: 0
        },
        balance_sheet: {
          cash: 0, accounts_receivable: 0, inventory: 0, total_current_assets: 0,
          property_equipment: 0, total_assets: 0, accounts_payable: 0, short_term_debt: 0,
          total_current_liabilities: 0, long_term_debt: 0, total_liabilities: 0,
          shareholders_equity: 0, retained_earnings: 0, total_equity: 0
        },
        cash_flow: {
          operating_cash_flow: 0, investing_cash_flow: 0, financing_cash_flow: 0,
          net_change_in_cash: 0, beginning_cash: 0, ending_cash: 0
        }
      });
      
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create statement');
    } finally {
      setLoading(false);
    }
  };

  const InputField = ({ label, value, onChange, readOnly = false }) => (
    <div className="space-y-1">
      <Label className="text-xs text-[#1A3C34]/70">{label}</Label>
      <Input
        type="number"
        className={`h-9 border-[#1A3C34]/20 focus:border-[#1A3C34] font-mono text-sm ${readOnly ? 'bg-[#F4F4F0]' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-[#1A3C34]">Add Financial Statement</DialogTitle>
          <DialogDescription className="text-[#1A3C34]/60">
            Enter financial data for the period ({currency})
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-6">
              {/* Period Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#1A3C34]">Period *</Label>
                  <Input
                    placeholder="e.g., 2024-Q1, 2024-12"
                    className="border-[#1A3C34]/20 focus:border-[#1A3C34]"
                    value={formData.period}
                    onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                    required
                    data-testid="period-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1A3C34]">Period Type</Label>
                  <Select
                    value={formData.period_type}
                    onValueChange={(value) => setFormData({ ...formData, period_type: value })}
                  >
                    <SelectTrigger className="border-[#1A3C34]/20" data-testid="period-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Tabs defaultValue="income" className="w-full">
                <TabsList className="w-full grid grid-cols-3">
                  <TabsTrigger value="income">Income Statement</TabsTrigger>
                  <TabsTrigger value="balance">Balance Sheet</TabsTrigger>
                  <TabsTrigger value="cashflow">Cash Flow</TabsTrigger>
                </TabsList>

                {/* Income Statement */}
                <TabsContent value="income" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      label="Revenue"
                      value={formData.income_statement.revenue}
                      onChange={(v) => updateIncomeStatement('revenue', v)}
                    />
                    <InputField
                      label="Cost of Goods Sold"
                      value={formData.income_statement.cost_of_goods_sold}
                      onChange={(v) => updateIncomeStatement('cost_of_goods_sold', v)}
                    />
                    <InputField
                      label="Gross Profit"
                      value={formData.income_statement.gross_profit}
                      onChange={() => {}}
                      readOnly
                    />
                    <InputField
                      label="Operating Expenses"
                      value={formData.income_statement.operating_expenses}
                      onChange={(v) => updateIncomeStatement('operating_expenses', v)}
                    />
                    <InputField
                      label="Operating Income"
                      value={formData.income_statement.operating_income}
                      onChange={() => {}}
                      readOnly
                    />
                    <InputField
                      label="Interest Expense"
                      value={formData.income_statement.interest_expense}
                      onChange={(v) => updateIncomeStatement('interest_expense', v)}
                    />
                    <InputField
                      label="Tax Expense"
                      value={formData.income_statement.tax_expense}
                      onChange={(v) => updateIncomeStatement('tax_expense', v)}
                    />
                    <InputField
                      label="Net Income"
                      value={formData.income_statement.net_income}
                      onChange={() => {}}
                      readOnly
                    />
                  </div>
                </TabsContent>

                {/* Balance Sheet */}
                <TabsContent value="balance" className="space-y-4 mt-4">
                  <h4 className="font-medium text-[#1A3C34] text-sm">Assets</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      label="Cash"
                      value={formData.balance_sheet.cash}
                      onChange={(v) => updateBalanceSheet('cash', v)}
                    />
                    <InputField
                      label="Accounts Receivable"
                      value={formData.balance_sheet.accounts_receivable}
                      onChange={(v) => updateBalanceSheet('accounts_receivable', v)}
                    />
                    <InputField
                      label="Inventory"
                      value={formData.balance_sheet.inventory}
                      onChange={(v) => updateBalanceSheet('inventory', v)}
                    />
                    <InputField
                      label="Property & Equipment"
                      value={formData.balance_sheet.property_equipment}
                      onChange={(v) => updateBalanceSheet('property_equipment', v)}
                    />
                    <InputField
                      label="Total Current Assets"
                      value={formData.balance_sheet.total_current_assets}
                      onChange={() => {}}
                      readOnly
                    />
                    <InputField
                      label="Total Assets"
                      value={formData.balance_sheet.total_assets}
                      onChange={() => {}}
                      readOnly
                    />
                  </div>
                  
                  <h4 className="font-medium text-[#1A3C34] text-sm pt-2">Liabilities</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      label="Accounts Payable"
                      value={formData.balance_sheet.accounts_payable}
                      onChange={(v) => updateBalanceSheet('accounts_payable', v)}
                    />
                    <InputField
                      label="Short-term Debt"
                      value={formData.balance_sheet.short_term_debt}
                      onChange={(v) => updateBalanceSheet('short_term_debt', v)}
                    />
                    <InputField
                      label="Long-term Debt"
                      value={formData.balance_sheet.long_term_debt}
                      onChange={(v) => updateBalanceSheet('long_term_debt', v)}
                    />
                    <InputField
                      label="Total Liabilities"
                      value={formData.balance_sheet.total_liabilities}
                      onChange={() => {}}
                      readOnly
                    />
                  </div>

                  <h4 className="font-medium text-[#1A3C34] text-sm pt-2">Equity</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      label="Shareholders' Equity"
                      value={formData.balance_sheet.shareholders_equity}
                      onChange={(v) => updateBalanceSheet('shareholders_equity', v)}
                    />
                    <InputField
                      label="Retained Earnings"
                      value={formData.balance_sheet.retained_earnings}
                      onChange={(v) => updateBalanceSheet('retained_earnings', v)}
                    />
                    <InputField
                      label="Total Equity"
                      value={formData.balance_sheet.total_equity}
                      onChange={() => {}}
                      readOnly
                    />
                  </div>
                </TabsContent>

                {/* Cash Flow */}
                <TabsContent value="cashflow" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <InputField
                      label="Operating Cash Flow"
                      value={formData.cash_flow.operating_cash_flow}
                      onChange={(v) => updateCashFlow('operating_cash_flow', v)}
                    />
                    <InputField
                      label="Investing Cash Flow"
                      value={formData.cash_flow.investing_cash_flow}
                      onChange={(v) => updateCashFlow('investing_cash_flow', v)}
                    />
                    <InputField
                      label="Financing Cash Flow"
                      value={formData.cash_flow.financing_cash_flow}
                      onChange={(v) => updateCashFlow('financing_cash_flow', v)}
                    />
                    <InputField
                      label="Net Change in Cash"
                      value={formData.cash_flow.net_change_in_cash}
                      onChange={() => {}}
                      readOnly
                    />
                    <InputField
                      label="Beginning Cash"
                      value={formData.cash_flow.beginning_cash}
                      onChange={(v) => updateCashFlow('beginning_cash', v)}
                    />
                    <InputField
                      label="Ending Cash"
                      value={formData.cash_flow.ending_cash}
                      onChange={() => {}}
                      readOnly
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </ScrollArea>

          <div className="flex justify-end gap-3 pt-4 border-t mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-[#1A3C34]/20 text-[#1A3C34]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#1A3C34] hover:bg-[#1A3C34]/90 text-white"
              disabled={loading}
              data-testid="save-statement-btn"
            >
              {loading ? 'Saving...' : 'Save Statement'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddStatementModal;
